import { Component, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, NgClass, NgFor, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CartItem } from '../../components/cart-item/cart-item';
import { DecimalPipe } from '@angular/common';
import { faCartArrowDown, faCartPlus, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { CartService } from '../../service/cart-service';
import { SelectedProduct } from '../../models/selected-product';
import { OrderService } from '../../service/order-service';
import { Order, OrderStatus, OrderType } from '../../models/order';
import { AuthService } from '../../service/auth-service';
import { firstValueFrom, skip, Subject, take, takeUntil } from 'rxjs';
import { ShippingDetails, User } from '../../models/user';
import { CITIES_BY_PROVINCE, COUNTRIES, PROVINCES } from '../../models/location.data';
import { LoadingButton } from '../../components/loading-button/loading-button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ShippingService } from '../../service/shipping-service';
import { ItemsNotFound } from '../../components/items-not-found/items-not-found';
import { faCreditCard } from '@fortawesome/free-regular-svg-icons';
import { PaymentService } from '../../service/payment-service';
import { Router, RouterLink } from '@angular/router';
import { ItemsLoading } from '../../components/items-loading/items-loading';
import { Analytics, logEvent } from '@angular/fire/analytics';
@Component({
  selector: 'app-cart',
  imports: [FormsModule, CartItem, DecimalPipe, NgIf, NgFor, FontAwesomeModule, ReactiveFormsModule, NgClass, LoadingButton, ItemsNotFound, ItemsLoading, RouterLink],
  templateUrl: './cart.html',
  styleUrl: './cart.scss'
})
export class Cart implements OnInit, OnDestroy {
  faShoppingCart = faShoppingCart;
  faWhatsapp = faWhatsapp;
  faCartPlus = faCartPlus
  faCreditCard = faCreditCard
  private cartService = inject(CartService);
  private orderService = inject(OrderService)
  private authService = inject(AuthService)
  private shippingService = inject(ShippingService)
  private paymentService = inject(PaymentService)
  private analytics = inject(Analytics);
  protected total = 0;
  cartItems: SelectedProduct[] | null = null;
  loading: boolean = true;
  subtotal = 0;
  shipping = 0;
  protected loadingWhatsApp = false;
  protected loadingpayhere = false;
  protected loadingkoko = false;
  protected termsAccepted: boolean = false;
  private fb = inject(FormBuilder)
  protected checkoutForm!: FormGroup;
  countries = COUNTRIES;
  provinces = PROVINCES;
  citiesByProvince = CITIES_BY_PROVINCE;
  availableCities: string[] = [];
  private snackBar = inject(MatSnackBar)
  protected paymentMethod: OrderType = OrderType.BANK;
  protected serviceCharge = 0;
  protected discount = 0;
  protected couponMessage = '';
  protected paymentMethods: Record<
    string,
    {
      serviceCharge: number;
      serviceChargeType: 'percentage' | 'fixed';
      discount: number;
      discountType: 'percentage' | 'fixed';
    } | undefined
  > = {};
  private router = inject(Router)
  private destroy$ = new Subject<void>();
  private platformId = inject(PLATFORM_ID);
  showBankDetails = false;

  toggleBankDetails() {
    this.showBankDetails = !this.showBankDetails;
  }

  constructor() {
    this.checkoutForm = this.fb.group({
      fullName: ['', Validators.required],
      address: ['', Validators.required],
      country: ['Sri Lanka', Validators.required],
      province: ['', Validators.required],
      city: [{ value: '', disabled: true }, Validators.required],
      postalCode: [
        '',
        [Validators.required, Validators.pattern(/^[0-9]{5}$/)]
      ],
      phone: [
        '',
        [Validators.required, Validators.pattern(/^[0-9]{10,15}$/)]
      ],
    });

    this.checkoutForm.get('province')?.valueChanges.subscribe(province => {
      this.availableCities = this.citiesByProvince[province] || [];

      const cityControl = this.checkoutForm.get('city');
      cityControl?.reset();
      cityControl?.enable();
      cityControl?.setValue(this.availableCities.length > 0 ? this.availableCities[0] : '');
      this.updateShippingPrice();
    });

    this.checkoutForm.get('city')?.valueChanges.subscribe(() => {
      this.updateShippingPrice();
    });

    this.authService.user$.subscribe((user: User | null | undefined) => {
      if (user) {
        this.checkoutForm.setValue({
          'fullName': user?.name || '',
          'phone': user?.contactNo || '',
          'address': user.shippingDetails?.address || '',
          'country': user.shippingDetails?.country || '',
          'province': user.shippingDetails?.province || '',
          'city': user.shippingDetails?.city || '',
          'postalCode': user.shippingDetails?.postalCode || '',
        });
        this.updateShippingPrice();
      }
    });

    this.paymentService.getAllServiceChargeAndDiscount().subscribe(res => {
      this.paymentMethods = res;
    }, error => {
      console.error('Error fetching payment methods:', error);
      this.paymentMethods = {};
    });

    this.onPaymentMethodChange(this.paymentMethod);

  }

  applyCoupon() {

    // if (this.couponCode.toLowerCase() === 'save10') {
    //   this.discount = this.subtotal * 0.10;
    //   this.couponMessage = 'Coupon applied successfully! 10% discount added.';
    // }

    // else if (this.couponCode.toLowerCase() === 'flat500') {
    //   this.discount = 500;
    //   this.couponMessage = 'Rs.500 discount applied.';
    // }

    // else {
    //   this.discount = 0;
    //   this.couponMessage = 'Invalid coupon code';
    // }

  }

  updateShippingPrice() {
    this.shippingService.getShippingPrice(
      this.checkoutForm.get('province')?.value,
      this.checkoutForm.get('city')?.value
    ).subscribe(price => {
      this.shipping = price;
      this.onPaymentMethodChange(this.paymentMethod); // Recalculate service charge and discount based on new shipping
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      logEvent(this.analytics, 'cart_page_view', { page_path: '/cart' });
    }
    this.cartService.cart$.pipe(takeUntil(this.destroy$)).subscribe(items => {
      this.cartItems = items;
      if (this.loading) {
        this.loading = false; // stop loader only once
      }

      this.subtotal = this.cartService.calculateTotal();
      this.total = this.subtotal + this.shipping;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async orderViaBank() {
    this.loadingWhatsApp = true;
    let message = '*Hello!* I would like to place an order for the following items:\n\n';
    this.cartItems!.forEach((item, index) => {
      const p = item; // or item if selectedProduct merged
      message += `*${index + 1}.* ${p.product.name}\n`;
      if (p.color?.name) message += `    Color: ${p.color.name}\n`;
      if (p.size) message += `    Size: ${p.size}\n`;
      message += `    Price: Rs.${p.price.toLocaleString()}\n`;
      message += `    Quantity: ${item.quantity}\n\n`;
    });

    const total = this.cartItems!.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    message += `----------------------\n`;
    message += `Subtotal: Rs.${total.toLocaleString()}\n`;
    message += `Shipping: Rs.${this.shipping.toLocaleString()}\n`;
    message += `*Total:* Rs.${(total + this.shipping).toLocaleString()}\n`;
    message += `\nPlease confirm my order. Thank you! `;

    const phone = '94723166611'; // WhatsApp number without '+' symbol
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    const shippingDetails: ShippingDetails = {
      fullName: this.checkoutForm.get('fullName')?.value,
      address: this.checkoutForm.get('address')?.value,
      country: this.checkoutForm.get('country')?.value,
      province: this.checkoutForm.get('province')?.value,
      city: this.checkoutForm.get('city')?.value,
      postalCode: this.checkoutForm.get('postalCode')?.value,
      contactNo: this.checkoutForm.get('phone')?.value
    };

    const user = await firstValueFrom(this.authService.user$)
    const order: Order = {
      id: '',
      orderId: '',
      userId: user!.id!,
      trackingNumber: '',
      products: this.cartItems!,
      subTotal: this.subtotal,
      shippingFee: this.shipping,
      total: this.subtotal + this.shipping + this.serviceCharge - this.discount,
      serviceCharge: this.serviceCharge,
      discount: this.discount,
      user: user!,
      shippingDetails: shippingDetails,
      status: OrderStatus.PENDING,
      type: OrderType.BANK,
      termsAndConditions: this.termsAccepted,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    this.orderService.createOrderTransaction(order).subscribe(res => {
      this.cartService.clearCart().subscribe(() => {
        this.loadingWhatsApp = false;
        this.snackBar.open("Order Placed Successfully!", 'Close', { duration: 5000 });
        this.router.navigate(['/orders']); // navigate to orders page
      }, (error) => {
        this.loadingWhatsApp = false;
        this.snackBar.open("Order Placed but can't clear cart!", 'Close', { duration: 5000 });
        console.error('Error clearing cart after order transaction:', error);
      })
    }, (error) => {
      this.loadingWhatsApp = false;
      this.snackBar.open("Can't Place Order!", 'Close', { duration: 5000 });
      console.error('Error creating order transaction:', error);
    })
  }

  orderViaKoko() {

  }

  orderViaPayHere() {

  }

  async onPaymentMethodChange(method: string) {
    this.paymentService.getServiceChargeAndDiscount(method as OrderType).subscribe(res => {
      this.serviceCharge = res.serviceChargeType === 'percentage' ? (res.serviceCharge / 100) * (this.subtotal + this.shipping) : res.serviceCharge;
      this.discount = res.discountType === 'percentage' ? (res.discount / 100) * (this.subtotal + this.shipping) : res.discount;
    }, error => {
      console.error('Error fetching service charge and discount:', error);
      this.serviceCharge = 0;
      this.discount = 0;
    });
  }

  // async placeOrder() {
  //   const shippingDetails: ShippingDetails = {
  //     fullName: this.checkoutForm.get('fullName')?.value,
  //     address: this.checkoutForm.get('address')?.value,
  //     country: this.checkoutForm.get('country')?.value,
  //     province: this.checkoutForm.get('province')?.value,
  //     city: this.checkoutForm.get('city')?.value,
  //     postalCode: this.checkoutForm.get('postalCode')?.value
  //   };
  //   const user = await firstValueFrom(this.authService.user$)
  //   const order: Order = {
  //     id: '',
  //     orderId: '',
  //     trackingNumber: '',
  //     userId: user!.id!,
  //     products: this.cartItems,
  //     subTotal: this.subtotal,
  //     shippingFee: this.shipping,
  //     total: this.total + this.shipping,
  //     user: user!,
  //     shippingDetails: shippingDetails,
  //     status: OrderStatus.PENDING,
  //     type: OrderType.WEB,
  //     createdAt: new Date().toISOString(),
  //     updatedAt: new Date().toISOString()
  //   }
  //   // this.orderService.createOrderTransaction(order).subscribe(res => {
  //   //   this.cartService.clearCart().subscribe(() => {
  //   //     alert('Order placed successfully!');
  //   //   }, (error) => {
  //   //     console.error('Error clearing cart after order transaction:', error);
  //   //   })
  //   // }, (error) => {
  //   //   console.error('Error creating order transaction:', error);
  //   // })
  // }

  async payNow() {
    if (!this.cartItems || this.cartItems.length === 0) {
      this.snackBar.open("Your cart is empty!", 'Close', { duration: 5000 });
      return;
    }

    const available = await firstValueFrom(this.orderService.checkCartAvailability(this.cartItems));
    if (available.available === false) {
      this.snackBar.open("Some items in your cart are no longer available. Please review your cart.", 'Close', { duration: 5000 });
      return;
    }

    if (this.authService.currentUser?.emailVerified === false) {
      this.router.navigate(['/verify-email'], { queryParams: { returnUrl: '/cart' } });
      return;
    }
    if (this.paymentMethod === OrderType.BANK) {
      this.orderViaBank();
    } else if (this.paymentMethod === OrderType.KOKO) {
      this.orderViaKoko();
    }
    else if (this.paymentMethod === OrderType.PAYHERE) {
      this.orderViaPayHere();
    }
  }

  // increaseQuantity(item: SelectedProduct) {
  //   const qty = item.quantity + 1;
  //   this.cartService.updateQuantity(item.id!, qty).subscribe((res) => {
  //     this.snackBar.open("Quantity increased!", 'Close', { duration: 5000 });
  //   }, (error) => {
  //     this.snackBar.open("Failed to increase quantity!", 'Close', { duration: 5000 });
  //   });
  // }

  increaseQuantity(item: SelectedProduct) {

    const newItem: SelectedProduct = {
      ...item,
      quantity: item.quantity + 1
    };

    //  STEP 1: Check availability BEFORE updating
    this.orderService.checkProductAvailability(newItem).subscribe({
      next: (res) => {
        if (!res.available) {
          this.snackBar.open(res.message || 'Out of stock', 'Close', {
            duration: 4000
          });
          return;
        }

        // STEP 2: Only update if valid
        this.cartService.updateQuantity(item.id!, newItem).subscribe({
          next: () => {
            this.onPaymentMethodChange(this.paymentMethod); 
            this.snackBar.open('Quantity increased!', 'Close', {
              duration: 3000
            });
          },
          error: () => {
            this.snackBar.open('Failed to update quantity!', 'Close', {
              duration: 3000
            });
          }
        });
      },
      error: () => {
        this.snackBar.open('Stock check failed!', 'Close', {
          duration: 3000
        });
      }
    });
  }

  // decreaseQuantity(item: any) {
  //   if (item.quantity > 1) {
  //     const qty = item.quantity - 1;
  //     this.cartService.updateQuantity(item.id!, qty).subscribe((res) => {
  //       this.snackBar.open("Quantity decreased!", 'Close', { duration: 5000 });
  //     }, (error) => {
  //       this.snackBar.open("Failed to decrease quantity!", 'Close', { duration: 5000 });
  //     });
  //   };
  // }

  decreaseQuantity(item: SelectedProduct) {
    if (item.quantity <= 1) {
      // Optional: show message if user tries to go below 1
      this.snackBar.open('Minimum quantity is 1', 'Close', { duration: 3000 });
      return;
    }

    const newItem: SelectedProduct = {
      ...item,
      quantity: item.quantity - 1
    };

    this.cartService.updateQuantity(item.id!, newItem).subscribe({
      next: () => {
        this.onPaymentMethodChange(this.paymentMethod); 
        this.snackBar.open('Quantity decreased!', 'Close', { duration: 3000 });
      },
      error: () => {
        this.snackBar.open('Failed to update quantity!', 'Close', { duration: 3000 });
      }
    });
  }

  removeFromCart(item: any) {
    // this.cartItems = this.cartItems.filter(i => i.id !== item.id);
    this.cartService.removeFromCart(item.id!).subscribe(res => {
      this.snackBar.open("Item removed from cart successfully!", 'Close', { duration: 5000 });
      this.onPaymentMethodChange(this.paymentMethod); 
    }, (error) => {
      this.snackBar.open("Failed to remove item from cart!", 'Close', { duration: 5000 });
    });
  }
}
